import { useCallback, useMemo, useState } from "react";
import type { EventRow } from '../components/EventsManagementView/types/EventsOverviewTypes';
import type { DeviceEventInfo } from './types/HookTypes';
import { formatErrorMessage, showToastError, showToastErrorMessage } from '../helpers/toastError';

export type { EventRow, DeviceEventInfo };

export default function useNonTerminalDeviceEventAssociations(
  selectedDeviceIds: string[],
  options?: {
    listEventsLimit?: number;
    listEventDevicesLimit?: number;
  },
): {
  events: EventRow[];
  eventsLoading: boolean;
  eventsError: string | null;
  deviceEventMap: Record<string, DeviceEventInfo | undefined>;
  reload: () => Promise<void>;
} {
  const listEventsLimit = options?.listEventsLimit ?? 200;
  const listEventDevicesLimit = options?.listEventDevicesLimit ?? 5000;

  const selectedIdsKey = useMemo(
    () =>
      selectedDeviceIds
        .map((id) => String(id ?? "").trim())
        .filter(Boolean)
        .sort()
        .join("\u0001"),
    [selectedDeviceIds],
  );

  const [events, setEvents] = useState<EventRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [deviceEventMap, setDeviceEventMap] = useState<
    Record<string, DeviceEventInfo | undefined>
  >({});

  const reload = useCallback(async (): Promise<void> => {
    if (!window.eventsAPI) {
      const msg = "Events API is not available";
      showToastErrorMessage(msg);
      setEventsError(msg);
      return;
    }

    setEventsLoading(true);
    setEventsError(null);

    try {
      const eventsRes = await window.eventsAPI.listEvents({ limit: listEventsLimit });

      if (!eventsRes.ok) {
        const msg = `Failed to load events (${eventsRes.status ?? "error"})`;
        setEventsError(msg);
        showToastErrorMessage(msg);
        return;
      }

      const data = eventsRes.data as any;
      const rows: EventRow[] = Array.isArray(data?.events) ? data.events : [];
      setEvents(rows);

      const ids = selectedIdsKey ? selectedIdsKey.split("\u0001") : [];
      const selectedSet = new Set<string>(ids);

      const buildLegacyDeviceEventMap = async (): Promise<Record<string, DeviceEventInfo>> => {
        const m: Record<string, DeviceEventInfo> = {};
        for (const ev of rows) {
          const st = String(ev.status ?? "").trim().toUpperCase();
          if (st === "COMPLETE" || st === "DELETED") continue;

          const devsRes = await window.eventsAPI.listEventDevices(ev.eventId, { limit: listEventDevicesLimit });
          if (!devsRes.ok) continue;

          const devData = devsRes.data as any;
          const devRows: { deviceId: string }[] = Array.isArray(devData?.devices)
            ? devData.devices
            : [];

          for (const d of devRows) {
            if (!selectedSet.has(d.deviceId)) continue;
            if (m[d.deviceId]) continue;

            m[d.deviceId] = {
              eventId: ev.eventId,
              name: ev.name,
              status: ev.status,
            };
          }
        }
        return m;
      };

      let activeMap: Record<string, DeviceEventInfo> = {};
      if (ids.length === 0) {
        setDeviceEventMap({});
        return;
      }

      const q = await window.eventsAPI.queryDeviceEventAssignments(ids);
      if (q.ok) {
        const qdata = q.data as {
          assignments?: Array<{
            deviceId?: string;
            eventId?: string;
            raceId?: string;
            name?: string;
            status?: string;
          }>;
        };
        for (const a of qdata?.assignments ?? []) {
          const did = String(a?.deviceId ?? "").trim();
          if (!did) continue;
          activeMap[did] = {
            eventId: String(a?.eventId ?? "").trim(),
            raceId: String(a?.raceId ?? "").trim() || undefined,
            name: a?.name,
            status: a?.status,
          };
        }
      } else {
        activeMap = await buildLegacyDeviceEventMap();
      }
      setDeviceEventMap(activeMap);
    } catch (err: any) {
      const msg = formatErrorMessage(err, "Failed to load events");
      setEventsError(msg);
      showToastErrorMessage(msg);
    } finally {
      setEventsLoading(false);
    }
  }, [listEventsLimit, listEventDevicesLimit, selectedIdsKey]);

  return { events, eventsLoading, eventsError, deviceEventMap, reload };
}

